/**
 * MilestoneMigration — admin utility to copy Airtable milestones + deliverable types into Supabase
 * (Stage 1 of the scheduling migration). One-click trigger for the idempotent import
 * (api/milestones-import.js). Re-runnable (upsert on airtable_id). The app still reads Airtable until
 * the service is flipped; this only populates Supabase. See docs/SCHEDULING_MIGRATION.md.
 */
import { useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

export default function MilestoneMigration({ dark }) {
  const [busy, setBusy]     = useState(false)
  const [result, setResult] = useState(null)
  const [err, setErr]       = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function call(method) {
    if (method === 'POST' && !(await confirmDialog({ title: 'Copy milestones to Supabase?', message: 'Copy all Executed-project milestones + deliverable types into Supabase now? Safe to re-run — it upserts, no duplicates. (Does not change what the app reads/writes yet.)', confirmLabel: 'Run import' }))) return
    setBusy(true); setErr(null); setResult(null)
    try {
      const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
      const res = await fetch('/api/milestones-import', { method, headers: token ? { Authorization: 'Bearer ' + token } : {} })
      const d = await res.json()
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
      setResult(d)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  const msMatch = result && result.airtableMilestones === result.supabaseMilestones

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 620 }}>
        Copies Executed-project milestones into <code>schedule_milestones</code> and the deliverable types
        into <code>deliverable_types</code> — the data-preservation step before the read-cutover. The app
        still reads Airtable for now. Idempotent: re-run any time (and again right before cutover) to sync.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn(false)} disabled={busy} onClick={() => call('GET')}>{busy ? 'Working…' : 'Check counts (dry run)'}</button>
        <button style={btn(true)} disabled={busy} onClick={() => call('POST')}>{busy ? 'Working…' : 'Run import'}</button>
      </div>
      {err && <div style={{ marginTop: 12, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim, lineHeight: 1.6 }}>
          {result.dryRun
            ? `Dry run — milestones Airtable ${result.airtableMilestones} · Supabase ${result.supabaseMilestones} · types Airtable ${result.airtableTypes} · Supabase ${result.supabaseTypes}`
            : `✓ Imported ${result.importedMilestones} milestones + ${result.importedTypes} types · ` +
              `Supabase now: ${result.supabaseMilestones} milestones / ${result.supabaseTypes} types` +
              (msMatch ? ' · counts match' : ' · ⚠ milestone counts differ')}
        </div>
      )}
    </div>
  )
}
